/**
 * HTML sanitization utilities for react-html-parser
 * Provides safe HTML parsing with tag and attribute filtering
 */

// Allowed HTML tags - whitelist approach for security
const ALLOWED_TAGS = [
	// Text formatting
	'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup',
	// Headings
	'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
	// Lists
	'ul', 'ol', 'li',
	// Tables
	'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
	// Links and media
	'a', 'img',
	// Structure
	'div', 'span', 'section', 'article',
	// Other common safe tags
	'blockquote', 'code', 'pre', 'hr',
];

// Allowed attributes - whitelist approach
const ALLOWED_ATTRIBUTES = {
	'*': ['class', 'id', 'style'],
	'a': ['href', 'target', 'rel', 'title'],
	'img': ['src', 'alt', 'width', 'height', 'title'],
	'td': ['colspan', 'rowspan'],
	'th': ['colspan', 'rowspan'],
};

// Protocol-safe URLs
const SAFE_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * Checks if a URL protocol is safe
 */
function isSafeProtocol(url) {
	try {
		const parsed = new URL(url);
		return SAFE_PROTOCOLS.includes(parsed.protocol);
	} catch {
		return false;
	}
}

/**
 * Sanitizes HTML string by removing dangerous tags and attributes
 * @param {string} html - HTML string to sanitize
 * @returns {string} Sanitized HTML
 */
export function sanitizeHtml(html) {
	if (typeof html !== 'string') {
		return '';
	}

	// Remove script tags and their content
	let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

	// Remove style tags and their content
	sanitized = sanitized.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');

	// Remove dangerous event handlers (onclick, onerror, etc.)
	sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');

	// Remove javascript: and data: protocols from href attributes
	sanitized = sanitized.replace(/href\s*=\s*["']\s*(javascript:|data:)[^"']*["']/gi, 'href="#"');

	// Remove iframe tags (common XSS vector)
	sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');

	// Remove object and embed tags
	sanitized = sanitized.replace(/<(object|embed)\b[^<]*(?:(?!<\/\1>)<[^<]*)*<\/\1>/gi, '');

	// Remove meta refresh tags
	sanitized = sanitized.replace(/<meta\s+http-equiv\s*=\s*["']refresh["'][^>]*>/gi, '');

	return sanitized;
}

/**
 * Transform function for react-html-parser
 * Filters out unsafe tags and attributes
 */
export function createHtmlTransform() {
	return (node, index) => {
		// Filter out nodes that are not in allowed tags
		if (node.type === 'tag' && !ALLOWED_TAGS.includes(node.name)) {
			return null;
		}

		// Filter out unsafe attributes
		if (node.type === 'tag' && node.attribs) {
			const allowedAttrs = ALLOWED_ATTRIBUTES[node.name] || ALLOWED_ATTRIBUTES['*'];
			const sanitizedAttribs = {};

			for (const [key, value] of Object.entries(node.attribs)) {
				// Check if attribute is allowed
				if (allowedAttrs.includes(key)) {
					// Special handling for href attribute
					if (key === 'href' && value) {
						if (isSafeProtocol(value)) {
							sanitizedAttribs[key] = value;
						}
					} else {
						sanitizedAttribs[key] = value;
					}
				}
			}

			node.attribs = sanitizedAttribs;
		}

		// In react-html-parser, returning undefined tells the parser to continue
		// default conversion to React elements. Returning the raw `node` causes React
		// to crash with: "Objects are not valid as a React child (found: object with keys {data, type, next, prev, parent})".
		return undefined;
	};
}

/**
 * Safe HTML parser wrapper
 * @param {string} html - HTML string to parse
 * @param {Function} transform - Optional custom transform function
 * @returns {Array} Parsed and sanitized nodes
 */
export function safeHtmlParser(html, transform) {
	if (typeof html !== 'string') {
		return [];
	}

	const sanitized = sanitizeHtml(html);
	const defaultTransform = createHtmlTransform();

	// Import react-html-parser dynamically to avoid SSR issues
	// This assumes react-html-parser is already imported in the calling component
	return {
		sanitizedHtml: sanitized,
		transform: transform || defaultTransform,
	};
}
